import * as React from 'react'
import { invariant } from '@epic-web/invariant'
import { isHotkey } from 'is-hotkey'
import Link from 'next/link'
import { ArrowLeftIcon, Cross2Icon } from '@radix-ui/react-icons'
import { clsx } from 'clsx'

import { DroppableBottomFillZone, dndTreeNodeId } from './dnd'

import * as InstanceBlock from '../instance'
import * as ListBlock from '../list'
import * as RichTextBlock from '../rich-text'
import * as ReferenceBlock from '../reference'
import * as AgentBlock from '../agent'

import { getCommands } from '~/lib/tiptap/extensions/comment/utils'
import { useTabsStore } from '../../tabs/store'
import { useValidInstancesForBlock } from '~/hooks/use-valid-instances-for-block'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useSlashCommandStore as useCommentSlashCommandStore } from '~/lib/tiptap/extensions/comment/extension-slash-command'
import {
  ClientSelectionUpdater,
  useRepoReplicache,
} from '~/hooks-data/use-repo-replicache'
import { Blocks } from '..'
import {
  TabContentDefaultWrapper,
  tabContentId,
} from '../../../../tab-content/wrapper'
import { FullViewDocumentPropertiesPanel } from './properties-panel'
import { getId } from '@basehub/utils/id/index'
import {
  isEmpty,
  commentExtensionsForTransformations,
  plainTextToYDocBase64State,
} from '@basehub/tiptap-api-utils'
import {
  blockTypesRecord,
  getTitleBasedOnSelectedVariants,
  isBlockWithChildren,
  isComponentBlock,
  isDocumentBlock,
  isInstanceBlock,
  isListBlock,
  isRichTextBlock,
  isReferenceBlock,
  Block,
  getTitleUpdateOverride,
  isAgentBlock,
  isTypeForBlockWithChildren,
} from '@basehub/data/replicache/index'
import { areBlocksCompatible } from '@basehub/data/replicache/schema/block/compare'
import { BlockPropertiesPanelWrapper } from '../block-wrapper'
import {
  getBlockDisplayData,
  validateBlockNesting,
} from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { Portal } from '~/components/portal'
import {
  EditorMode,
  EditorModeProvider,
  useEditorMode,
} from '../../editor-mode-provider'
import {
  useIsExternalVisitor,
  useIsGuestInHisBranch,
} from '~/hooks-data/use-user-role'
import { useEditorKeyboardNavigation } from '~/hooks/use-editor-keyboard-navigation'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  useBranchOrCommitSegment,
  useSelectedBranch,
} from '~/lib/commits/use-selected-branch-or-commit'
import { useRepoStore } from '../../repo-store'
import {
  EditorStoreProvider,
  createEditorStore,
  useEditorStore,
} from '../../editor-store'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import {
  RepoUndoStackItem,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import {
  CreateBlockParams,
  UpdateBlockParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { useIsPropertiesPanelCollapsed__PUBLIC } from '../../properties-panel/pp-layout'
import { useSearchParams } from 'next/navigation'
import {
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import ReactTextareaAutosize from 'react-textarea-autosize'
import { useCustomSyncerSub } from '~/replicache/frontend/use-custom-syncer-sub'
import { usePlaygroundCookie } from '~/lib/playground/use-playground-id'

// -------- -------
// Lazy imports
// -------- -------

const CommentsPopover = React.lazy(() => import('../../../../comments'))
const OthersInBlock = React.lazy(
  () => import('../block-wrapper-actions/others-in-block')
)
const InstanceNavigation = React.lazy(
  () => import('./actions/instance-navigation')
)

const debug = false

const DocumentBlockInner = ({
  blockId,
  block,
  parentBlock,
}: {
  blockId: Block['id']
  block: Block
  parentBlock: Block | undefined
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const blockTreeManager = useWorkingTreeStore((s) => s.tree)

  const activeTab = useTabsStore((s) => s.activeTab)
  // set tab into "permanent" if the document has been edited
  const [prevBlock, setPrevBlock] = React.useState<Block | null>(null)
  const markTabAsPermanent = useTabsStore((s) => s.markTabAsPermanent)
  const sourceBlockId = block ? idToSourceBlockId(block) : ''
  const path = blockTreeManager?.getPath(sourceBlockId, { format: 'string' })
  const blockRef = React.useRef(block)
  // eslint-disable-next-line react-compiler/react-compiler
  blockRef.current = block
  const parentBlockRef = React.useRef(parentBlock)
  // eslint-disable-next-line react-compiler/react-compiler
  parentBlockRef.current = parentBlock
  const blockChildrenKeys = block
    ? (blockTreeManager?.getChildrenKeys(block.id) ?? [])
    : []
  const isExternalVisitor = useIsExternalVisitor()
  const [isHoveringTitleInputContainer, setHoveringTitleInputContainer] =
    React.useState<boolean>(false)

  const { data: validInstancesForBlockData } = useValidInstancesForBlock({
    blockId,
    dependencies: [path],
    disableHiddenListTemplates: true,
  })

  if (parentBlock) {
    invariant(isBlockWithChildren(parentBlock), 'invalid parent type')
  }

  const setFocus = useRepoStore((s) => s.setFocus)
  const focusIsDocumentTitle = useRepoStore((s) =>
    block &&
    s.focus?.blockId === idToSourceBlockId(block) &&
    s.focus.type === 'block:document-title'
      ? s.focus
      : null
  )
  const focusIsWrapper = useRepoStore((s) =>
    block &&
    s.focus?.type === 'block:wrapper' &&
    blockChildrenKeys.some((bId) => bId === s.focus?.blockId)
      ? s.focus
      : null
  )
  const editMode = useUserPreferences((s) => s.editMode)
  const isGuestInHisBranch = useIsGuestInHisBranch()
  const editorMode = useEditorMode()
  const playgroundId = usePlaygroundCookie()
  const branch = useSelectedBranch().branch
  const isPlayground = Boolean(
    !!playgroundId && (!!branch?.playgroundId || branch?.isDefault)
  )
  const aiGeneration = useEditorStore((s) => s.aiGeneration)
  const setAiGeneration = useEditorStore((s) => s.setAiGeneration)
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)

  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const selectedDashboardVariants = useSelectedVariants({ parentBlock: block })

  const titleByVariant = React.useMemo(
    () =>
      block
        ? getTitleBasedOnSelectedVariants({
            block,
            variantsToFilterBy: selectedDashboardVariants,
            parent: parentBlock,
          }).title
        : '',
    [block, parentBlock, selectedDashboardVariants]
  )

  const { ref, onChange, value } = useSyncedValue<
    HTMLTextAreaElement,
    { value: string; parentStackItem: RepoUndoStackItem | null }
  >({
    value: React.useMemo(
      () => ({ value: titleByVariant, parentStackItem: null }),
      [titleByVariant]
    ),
    customEventsKey: `sync-document-title:${sourceBlockId}`,
    setValue: async ({ value, parentStackItem }, { internalValueRef }) => {
      const block = blockRef.current
      const parentBlock = parentBlockRef.current
      if (!block || !parentBlock || !replicache || !path) return
      const getParams = (v: string): UpdateBlockParams => {
        return {
          id: block.id,
          data: getTitleUpdateOverride({
            title: v,
            block,
            selectedVariants: selectedDashboardVariants,
            parent: parentBlock,
          }),
          instanceChildTarget: block.instanceChildTarget ?? null,
          strategy: 'merge-deep',
          path,
          isoNow: new Date().toISOString(),
        }
      }
      const result = await replicache.mutate.updateBlock(getParams(value))
      if (result.undo) {
        const stackItem: RepoUndoStackItem = {
          redo: [
            getBlockFocusOp({
              block: {
                id: block.id,
                sourceBlockId: block.sourceBlockId,
              },
              focusType: 'block:document-title',
              preferredTabKey: activeTabKey,
            }),
            {
              op: 'updateBlock',
              args: getParams(internalValueRef.current.value),
            },
            {
              op: 'customWindowEvent',
              args: {
                name: `sync-document-title:${sourceBlockId}`,
                detail: { action: 'sync' },
              },
            },
          ],
          undo: [
            getBlockFocusOp({
              block: {
                id: block.id,
                sourceBlockId: block.sourceBlockId,
              },
              focusType: 'block:document-title',
              preferredTabKey: activeTabKey,
            }),
            { op: 'blockBatchMutations', args: { mutations: result.undo } },
            {
              op: 'customWindowEvent',
              args: {
                name: `sync-document-title:${sourceBlockId}`,
                detail: { action: 'sync' },
              },
            },
          ],
        }

        if (parentStackItem) {
          parentStackItem.redo.push(...stackItem.undo)
          parentStackItem.undo.push(...stackItem.undo)
        } else {
          undoManager.current?.push(stackItem)
        }
      }
    },
  })

  const placeholderData = React.useMemo(() => {
    // is template
    if (
      block?.type === 'component' &&
      parentBlock?.type === 'list' &&
      parentBlock.typeOptions.targetBlockId === block.id &&
      block.hidden &&
      !block.title
    ) {
      return {
        placeholder: getBlockDisplayData({
          block,
          parentBlock,
          ignoreDisplayTitle: true,
        }).displayTitle,
        dimPlaceholder: false,
      }
    }
    return { placeholder: 'Untitled', dimPlaceholder: true }
  }, [block, parentBlock])

  const isKeyboardNavigationDisabled =
    !block ||
    blockChildrenKeys.length === 0 ||
    (isExternalVisitor && !isGuestInHisBranch && !isPlayground)

  const [backlink, setBacklink] = React.useState<{
    title: string
    id: string
    edgeId?: string
  } | null>(null)

  // focus textarea when document block is empty
  React.useLayoutEffect(() => {
    const focus = focusIsDocumentTitle ?? focusIsWrapper

    if (!block || editorMode === 'read' || !ref.current || !focus) {
      return
    }

    if (focusIsDocumentTitle) {
      setTimeout(() => {
        ref.current?.setSelectionRange(
          ref.current.value.length,
          ref.current.value.length
        )
        ref.current?.focus()
      })
    }

    if (focus.temporaryBackLink) {
      setBacklink(focus.temporaryBackLink)
    }

    setFocus(null)
  }, [block, focusIsDocumentTitle, focusIsWrapper, editorMode, setFocus, ref])

  const canCreateADocumentHere = React.useMemo(() => {
    if (!path) return false

    return validateBlockNesting({
      block: {
        type: blockTypesRecord.document,
        id: getId(),
        createdAt: new Date().toISOString(),
        value: { children: [] },
        hidden: false,
        isCommitted: false,
        title: 'Dummy doc',
        constraints: {},
        typeOptions: {},
        searchConfig: { index: false },
      },
      fetchBlock: (id) => blockTreeManager?.getNode(id)?.value,
      fetchChildren: (id) => {
        const keys = blockTreeManager?.getChildrenKeys(id)

        return (
          keys
            ?.map((k) => blockTreeManager?.getNode(k)?.value)
            .filter(Boolean) ?? []
        )
      },
      shouldThrowIfInvalid: false,
      path,
    }).isValid
  }, [path, blockTreeManager])

  const canCreateASetHere = React.useMemo(() => {
    if (!path) return false

    return validateBlockNesting({
      block: {
        type: blockTypesRecord.variantSet,
        id: getId(),
        createdAt: new Date().toISOString(),
        value: { variants: [] },
        hidden: false,
        isCommitted: false,
        title: 'Dummy set',
        constraints: {},
        typeOptions: {},
        searchConfig: { index: false },
      },
      fetchBlock: (id) => blockTreeManager?.getNode(id)?.value,
      fetchChildren: (id) => {
        const keys = blockTreeManager?.getChildrenKeys(id)

        return (
          keys
            ?.map((k) => blockTreeManager?.getNode(k)?.value)
            .filter(Boolean) ?? []
        )
      },
      shouldThrowIfInvalid: false,
      path,
    }).isValid
  }, [path, blockTreeManager])

  const canCreateAWorkflowHere = React.useMemo(() => {
    if (!path) return false

    return validateBlockNesting({
      block: {
        type: blockTypesRecord.workflow,
        id: getId(),
        createdAt: new Date().toISOString(),
        value: {
          trigger: {
            type: 'layout-block',
            blockIds: [],
            events: ['updated'],
            layoutBlockId: null,
          },
          activeRuns: [],
          actions: {},
        },
        hidden: false,
        isCommitted: false,
        title: 'Dummy workflow',
        constraints: {},
        typeOptions: {},
        searchConfig: { index: false },
      },
      fetchBlock: (id) => blockTreeManager?.getNode(id)?.value,
      fetchChildren: (id) => {
        const keys = blockTreeManager?.getChildrenKeys(id)

        return (
          keys
            ?.map((k) => blockTreeManager?.getNode(k)?.value)
            .filter(Boolean) ?? []
        )
      },
      shouldThrowIfInvalid: false,
      path,
    }).isValid
  }, [path, blockTreeManager])

  React.useEffect(() => {
    if (!validInstancesForBlockData?.validInstances) return

    const existingComponents = validInstancesForBlockData.validInstances

    const commands = getCommands({
      hide: {
        document: !canCreateADocumentHere,
        'variant-set': !canCreateASetHere,
        workflow: !canCreateAWorkflowHere,
      },
      existingComponents,
    })

    useCommentSlashCommandStore.setState({ commands })
  }, [
    validInstancesForBlockData?.validInstances,
    canCreateADocumentHere,
    canCreateASetHere,
    canCreateAWorkflowHere,
  ])

  React.useEffect(() => {
    if (!block || activeTab?.key !== block.id) return
    if (!prevBlock) {
      setPrevBlock(block)
      return
    }

    if (!areBlocksCompatible(prevBlock, block)) {
      // document has been edited, mark tab as permanent
      setPrevBlock(block)
      if (activeTab.partiallyOpen) {
        markTabAsPermanent(activeTab.key)
      }
    }
  }, [activeTab, block, markTabAsPermanent, prevBlock])

  useEditorKeyboardNavigation({
    block: block && isBlockWithChildren(block) ? block : undefined,
    path: path ?? undefined,
    disabled: isKeyboardNavigationDisabled,
  })

  useCustomSyncerSub(
    blockTreeManager && sourceBlockId ? replicache : undefined,
    {
      syncer: 'loadBlocks',
      params: {
        rootBlockId: blockTreeManager!.root.value.id,
        sourceBlockIds: [sourceBlockId],
      },
    }
  )

  const propPanelAutoFocus = useRepoStore((s) => {
    if (!s.focus) return null
    if (s.focus.type !== 'block:properties-panel') return null
    return s.focus
  })

  const userId = useUserPreferences((s) => s.id)
  const [isClosed_public, setIsClosed_public] =
    useIsPropertiesPanelCollapsed__PUBLIC()
  const collapsePropertiesPanel = useUserPreferences(
    (s) => s.collapsePropertiesPanel
  )

  React.useEffect(() => {
    if (!propPanelAutoFocus) return
    if (replicache && userId) {
      if (collapsePropertiesPanel) {
        replicache.mutate.setCollapsedPropertiesPanel({
          userId,
          isCollapsed: false,
        })
      }
    } else {
      if (isClosed_public) {
        setIsClosed_public(false)
      }
    }
    setFocus(null)
    setSelectedBlockId(propPanelAutoFocus.blockId)
    setTimeout(() => {
      if (propPanelAutoFocus.elementIdOverride) {
        const wrapper = document.querySelector(
          `[data-block-id="${propPanelAutoFocus.blockId}"]`
        )
        wrapper?.scrollIntoView({ block: 'nearest' })
        if (
          document.activeElement?.id !== propPanelAutoFocus.elementIdOverride &&
          !document.activeElement?.closest(
            `#${propPanelAutoFocus.elementIdOverride}`
          )
        ) {
          document.getElementById(propPanelAutoFocus.elementIdOverride)?.focus()
        }
      }
    }, 16)
  }, [
    collapsePropertiesPanel,
    isClosed_public,
    propPanelAutoFocus,
    replicache,
    setFocus,
    setIsClosed_public,
    setSelectedBlockId,
    userId,
  ])

  const searchParams = useSearchParams()
  const shouldFocusComment = searchParams.get('commentId')

  const [wasHoveredAtLeastOnce, setWasHoveredAtLeastOnce] =
    React.useState(false)

  React.useEffect(() => {
    if (isHoveringTitleInputContainer) {
      setWasHoveredAtLeastOnce(true)
    }
  }, [wasHoveredAtLeastOnce, isHoveringTitleInputContainer])

  const focusRTEditor = React.useCallback(() => {
    if (!sourceBlockId) return
    const editor = document.querySelector<HTMLDivElement>(
      `[data-block-id="${sourceBlockId}"] [contenteditable="true"]`
    )
    if (editor) {
      setTimeout(() => {
        editor.focus()
        // Also try setting selection
        const selection = window.getSelection()
        const range = document.createRange()
        range.selectNodeContents(editor)
        range.collapse(true) // Position cursor at start
        selection?.removeAllRanges()
        selection?.addRange(range)
      }, 0)
    }
  }, [sourceBlockId])

  const isRichTextFullView = block ? isRichTextBlock(block) : false
  React.useEffect(() => {
    if (isRichTextFullView) {
      const tId = setTimeout(() => {
        focusRTEditor()
      }, 0)
      return () => clearTimeout(tId)
    }
  }, [isRichTextFullView, focusRTEditor])

  if (!parentBlock || !blockTreeManager) {
    return <div className="flex-grow" />
  }

  const canHaveNewComments =
    !isInstanceBlock(block) &&
    !isListBlock(block) &&
    !isRichTextBlock(block) &&
    aiGeneration === null &&
    editorMode === 'write:all'

  if (block) {
    if (!isTypeForBlockWithChildren(block.type) && !isAgentBlock(block)) {
      console.error('invalid block type', block)
      return <div>Invalid tab type for block: {block.type}.</div>
    }
  }

  return (
    <>
      <ClientSelectionUpdater /> {/* for presence */}
      <div id={dndTreeNodeId} /> {/* for dnd */}
      <TabContentDefaultWrapper
        onPointerDown={(e) => {
          if (isRichTextBlock(block)) {
            setFocus({ blockId: idToSourceBlockId(block), type: 'block:value' })
            // manually focus prosemirror cause this ^ is not working
            if (
              e.target instanceof HTMLElement &&
              e.target.id === tabContentId
            ) {
              focusRTEditor()
            }
          } else {
            setSelectedBlockId(idToSourceBlockId(block))
          }
        }}
        className={clsx(
          '[&>div]:h-[var(--editor-height)]',
          isRichTextBlock(block) && '[&>div]:flex cursor-text'
        )}
        onFillerClick={async () => {
          if (!replicache || !path) return
          if (editMode) return
          if (isInstanceBlock(block)) return

          const lastBlockId = blockChildrenKeys[blockChildrenKeys.length - 1]

          if (lastBlockId) {
            const lastBlock = await replicache.query((tx) => {
              ensureBaseHubTransaction(tx)
              return blockBaseOps.get(tx, lastBlockId)
            })
            if (
              lastBlock?.type === 'comment' &&
              isEmpty(lastBlock.value.state)
            ) {
              // just focus it
              setFocus({
                type: 'block:value',
                blockId: idToSourceBlockId(lastBlock),
              })
              return
            } else if (isListBlock(block)) {
              setFocus({
                type: 'block:document-title',
                blockId: idToSourceBlockId(block),
              })
              return
            }
          }

          const id = getId()
          const params: CreateBlockParams = {
            data: { id, title: '', type: 'comment' },
            path,
            parentInstanceChildTarget: block.instanceChildTarget,
            position: { type: 'last' },
            isoNow: new Date().toISOString(),
            idSeed: getId(),
          }
          const result = await replicache.mutate.createBlock(params)
          setFocus({ type: 'block:value', blockId: id })
          if (result.undo) {
            undoManager.current?.push({
              redo: [
                { op: 'createBlock', args: params },
                getBlockFocusOp({
                  block: { id: id, sourceBlockId: id },
                  focusType: 'block:value',
                  preferredTabKey: activeTabKey,
                }),
              ],
              undo: [
                getBlockFocusOp({
                  block: { id: id, sourceBlockId: id },
                  focusType: 'block:value',
                  preferredTabKey: activeTabKey,
                }),
                {
                  op: 'blockBatchMutations',
                  args: { mutations: result.undo },
                },
              ],
            })
          }
        }}
        size={isListBlock(block) ? 'lg' : 'sm'}
        fullHeight={isRichTextBlock(block)}
        fillerChildren={
          !isRichTextBlock(block) && (
            <DroppableBottomFillZone
              intendedIndex={blockChildrenKeys.length}
              parentId={block.id}
              variant="default"
            />
          )
        }
      >
        <div
          className="relative bg-dark-base"
          onMouseEnter={() => setHoveringTitleInputContainer(true)}
          onMouseLeave={() => setHoveringTitleInputContainer(false)}
        >
          {/* Presence */}
          {!isExternalVisitor && (
            <React.Suspense fallback={null}>
              <OthersInBlock blockId={block.id} top={14} />
            </React.Suspense>
          )}
          {isListBlock(parentBlock) && (
            <React.Suspense fallback={null}>
              <div className="absolute w-full -top-2 translate-y-[-100%] left-0">
                <InstanceNavigation block={block} parentBlock={parentBlock} />
              </div>
            </React.Suspense>
          )}

          {isAgentBlock(block) || (
            <div
              className={clsx(
                'relative overflow-hidden h-full w-[calc(100%+16px)] flex flex-col',
                block.title !== titleByVariant ? 'pb-1' : 'pb-2'
              )}
            >
              <ReactTextareaAutosize
                data-document-title={idToSourceBlockId(block)}
                role="document-title"
                className={clsx(
                  'w-full h-full -mx-2 overflow-x-hidden overflow-y-auto scroll-my-48 placeholder:text-dark-faint resize-none rounded-md bg-transparent px-2 text-2xl font-medium leading-tight text-dark-bright focus:outline-none cursor-auto break-words whitespace-pre-wrap'
                )}
                placeholder={placeholderData.placeholder}
                value={value.value}
                onChange={async (e) => {
                  // remove line breaks
                  const value = e.target.value.replace(/[\r\n]+/g, '')
                  await onChange({ value, parentStackItem: null })
                }}
                disabled={
                  editorMode === 'read' || editorMode === 'write:value-only'
                }
                ref={ref}
                onKeyDown={async (e) => {
                  if (!path) return
                  if (
                    isHotkey('ArrowRight', e) &&
                    ref.current?.selectionStart === value.value.length
                  ) {
                    e.preventDefault()
                    e.stopPropagation()
                    const firstBlockId = blockChildrenKeys[0]

                    if (firstBlockId) {
                      e.currentTarget.blur()
                      setSelectedBlockId(
                        idToSourceBlockId(firstBlockId, blockTreeManager)
                      )
                    }
                  } else if (e.key === 'Enter') {
                    e.preventDefault()
                    e.stopPropagation()

                    const firstBlockId = blockChildrenKeys[0]
                    const firstChildIsComment =
                      firstBlockId &&
                      blockTreeManager.getNode(firstBlockId)?.value.type ===
                        'comment'

                    if (!canHaveNewComments || firstChildIsComment) {
                      if (firstBlockId) {
                        setFocus({
                          type: 'block:value',
                          blockId: idToSourceBlockId(
                            firstBlockId,
                            blockTreeManager
                          ),
                        })
                      }
                      return
                    }

                    if (blockChildrenKeys.length > 0) {
                      const selectionStart = ref.current?.selectionStart
                      const cuttedText = value.value.slice(
                        selectionStart,
                        value.value.length
                      )

                      const stackItem: RepoUndoStackItem = {
                        undo: [],
                        redo: [],
                      }

                      if (cuttedText) {
                        await onChange({
                          value: value.value.slice(0, selectionStart),
                          parentStackItem: stackItem,
                        })
                      }

                      const { base64: newBlockBase64State } =
                        await plainTextToYDocBase64State(
                          cuttedText ?? '',
                          commentExtensionsForTransformations
                        )
                      const id = getId()
                      const params: CreateBlockParams = {
                        data: {
                          id,
                          title: '',
                          type: 'comment',
                          value: {
                            state: newBlockBase64State,
                          },
                        },
                        parentInstanceChildTarget: block.instanceChildTarget,
                        path,
                        isoNow: new Date().toISOString(),
                        idSeed: getId(),
                        position: { type: 'first' },
                      }
                      const result =
                        await replicache?.mutate.createBlock(params)
                      setFocus({ type: 'block:value', blockId: id })
                      if (result?.undo) {
                        stackItem.redo.push(
                          { op: 'createBlock', args: params },
                          getBlockFocusOp({
                            block: result.newBlock,
                            focusType: 'block:value',
                            preferredTabKey: activeTabKey,
                          })
                        )
                        stackItem.undo.push(
                          getBlockFocusOp({
                            block: {
                              id: block.id,
                              sourceBlockId: block.sourceBlockId,
                            },
                            focusType: 'block:document-title',
                            preferredTabKey: activeTabKey,
                          }),
                          {
                            op: 'blockBatchMutations',
                            args: { mutations: result.undo },
                          }
                        )
                        undoManager.current?.push(stackItem)
                      }
                    } else {
                      // empty state will kick in.
                    }
                  }
                }}
                onFocus={() => {
                  setSelectedBlockId(idToSourceBlockId(block))
                  setAiGeneration(null)
                }}
                rows={1}
              />
              {block.title !== titleByVariant && (
                <div className="pointer-events-none text-xs font-medium !leading-normal text-dark-faint w-full tracking-normal h-5">
                  {block.title || 'Untitled'}
                </div>
              )}
            </div>
          )}
          <div className="flex flex-row inset-0 w-full h-full pointer-events-none select-none absolute">
            {/* placeholder element */}
            <span className="-mx-2 relative scroll-my-48 max-w-full placeholder:text-dark-faint -my-2 bg-transparent py-2 px-2 pb-6 text-2xl font-medium leading-tight">
              <span className="opacity-0 overflow-hidden whitespace-nowrap relative w-full">
                {value.value || placeholderData.placeholder}
              </span>
              {path && (wasHoveredAtLeastOnce || shouldFocusComment) && (
                <React.Suspense fallback={null}>
                  <CommentsPopover
                    className="pointer-events-auto cursor-default absolute left-[calc(100%+8px)] top-2 z-20 !translate-y-0"
                    side="right"
                    align="start"
                    type="block"
                    block={block}
                    isVisible={isHoveringTitleInputContainer}
                  />
                </React.Suspense>
              )}
            </span>
          </div>
        </div>

        {(isDocumentBlock(block) || isComponentBlock(block)) && !!path && (
          <>
            <Blocks block={block} path={path} />
            <FullViewDocumentPropertiesPanel
              block={block}
              path={path}
              parent={parentBlock}
              strict={false}
            />
          </>
        )}

        {isInstanceBlock(block) && !!path && (
          <>
            <InstanceBlock.Input
              block={block}
              path={path}
              blockTreeManager={blockTreeManager}
            />
            <InstanceBlock.FullViewPropertiesPanel
              block={block}
              path={path}
              parent={parentBlock}
              strict={false}
            />

            {debug && (
              <pre className="font-mono text-xs">
                {JSON.stringify(block, null, 2)}
              </pre>
            )}
          </>
        )}

        {isRichTextBlock(block) && !!path && (
          <>
            <div id={`more-actions-portal-${block.id}`} className="hidden" />
            <RichTextBlock.Input
              block={block}
              path={path}
              parent={parentBlock}
              fullSize
            />
            <RichTextBlock.FullViewPropertiesPanelWrapper
              block={block}
              parentBlock={parentBlock}
              path={path}
            >
              <RichTextBlock.PropertiesPanel
                block={block}
                path={path}
                parent={parentBlock}
              />
            </RichTextBlock.FullViewPropertiesPanelWrapper>
          </>
        )}

        {isReferenceBlock(block) && !!path && (
          <>
            <div className="w-full h-full inset-0 absolute p-[inherit] m-[inherit] flex items-start justify-end">
              <div
                id={`more-actions-portal-${block.id}`}
                className="flex pt-2"
              />
            </div>
            <ReferenceBlock.Input
              block={block}
              path={path}
              parent={parentBlock}
            />
            <BlockPropertiesPanelWrapper
              parentBlock={parentBlock}
              block={block}
              path={path}
              isFullView
            >
              <ReferenceBlock.PropertiesPanel
                block={block}
                path={path}
                parent={parentBlock}
              />
            </BlockPropertiesPanelWrapper>
          </>
        )}

        {isListBlock(block) && !!path && (
          <div>
            <div
              id={`more-actions-portal-${block.id}`}
              className="mb-2 sticky top-0 z-40"
            />
            <ListBlock.Input
              block={block}
              path={path}
              fullView
              parent={parentBlock}
            />
            <ListBlock.FullViewPropertiesPanelWrapper
              block={block}
              parentBlock={parentBlock}
              path={path}
            >
              <ListBlock.PropertiesPanel
                block={block}
                path={path}
                parent={parentBlock}
              />
            </ListBlock.FullViewPropertiesPanelWrapper>
          </div>
        )}

        {isAgentBlock(block) && !!path && (
          <div>
            <AgentBlock.Input block={block} path={path} parent={parentBlock} />
            <AgentBlock.FullViewPropertiesPanelWrapper
              block={block}
              parentBlock={parentBlock}
              path={path}
            >
              <AgentBlock.PropertiesPanel
                block={block}
                path={path}
                parent={parentBlock}
              />
            </AgentBlock.FullViewPropertiesPanelWrapper>
          </div>
        )}

        {backlink && (
          <BackLink
            {...backlink}
            onClose={() => {
              setBacklink(null)
            }}
          />
        )}
      </TabContentDefaultWrapper>
    </>
  )
}

export const DocumentBlock = ({ blockId }: { blockId: Block['id'] }) => {
  const blockNode = useWorkingTreeStore((s) =>
    s.tree?.getNode(blockId, { fallbackKey: 'sourceBlockId' })
  )
  const parentBlockNode = useWorkingTreeStore((s) => {
    const pkey = s.tree?.getNode(blockId)?.parentKey
    if (!pkey) return null
    return s.tree?.getNode(pkey)
  })
  const block = blockNode?.value
  const parentBlock = parentBlockNode?.value
  const isParentInstance = parentBlock ? isInstanceBlock(parentBlock) : false

  let editorMode: EditorMode = 'write:all'
  if (block && isInstanceBlock(block)) {
    editorMode = 'write:value-and-title'
  }
  if (isParentInstance) {
    editorMode = 'write:value-only'
  }

  const editorStore = React.useMemo(() => createEditorStore(), [])

  if (!block) return <div className="flex-grow" />

  return (
    <EditorModeProvider value={editorMode} parentBlock={block} block={null}>
      <EditorStoreProvider prebuiltStore={editorStore}>
        <DocumentBlockInner
          blockId={blockId}
          block={block}
          parentBlock={parentBlock}
        />
      </EditorStoreProvider>
    </EditorModeProvider>
  )
}

export const BackLink = ({
  title,
  id,
  edgeId,
  onClose,
  autoClose = false,
}: {
  title: string
  id: string
  edgeId?: string
  onClose: () => void
  autoClose?: boolean
}) => {
  const setFocus = useRepoStore((s) => s.setFocus)
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const [enter, setEnter] = React.useState(false)
  const [exit, setExit] = React.useState(false)

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setEnter(true)
    }, 500)

    return () => {
      clearTimeout(timeout)
    }
  }, [])

  React.useEffect(() => {
    if (!autoClose) return
    if (!enter) return

    const timeout = setTimeout(() => {
      setExit(true)
    }, 4800)

    return () => {
      clearTimeout(timeout)
    }
  }, [onClose, enter, autoClose])

  React.useEffect(() => {
    if (!exit) return
    const timeout = setTimeout(() => {
      onClose()
    }, 5000)

    return () => {
      clearTimeout(timeout)
    }
  }, [exit, onClose])

  return (
    <Portal>
      <div
        className={clsx(
          'fixed bottom-16 w-full z-50 flex justify-center transition duration-150 pointer-events-none',
          (exit || !enter) && 'opacity-0 translate-y-5'
        )}
      >
        <div className="inline-flex overflow-hidden cursor-default items-center text-sm rounded-full border border-dark-border-solid bg-dark-shade h-[30px] shadow-modal pointer-events-auto">
          <Link
            onClick={() => {
              setFocus({
                type: edgeId ? 'block:wrapper' : 'block:document-title',
                blockId: edgeId ?? id,
              })
            }}
            href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${id}${
              edgeId ? `#${edgeId}` : ''
            }`}
            className="cursor-default flex items-center gap-1 px-2 h-full font-semibold hover:bg-dark-border-solid transition-colors"
          >
            <ArrowLeftIcon />
            <span className="font-medium">Return to {title || 'Untitled'}</span>
          </Link>
          <button
            onClick={() => setExit(true)}
            type="button"
            aria-label="Dismiss"
            className="px-1.5 h-full border-l border-dark-border-solid cursor-default hover:bg-dark-border-solid transition-colors"
          >
            <Cross2Icon />
          </button>
        </div>
      </div>
    </Portal>
  )
}
